import { useCallback, useMemo, useState } from "react";
import useUser from "./useUser";
import api from "../api";

const useOnboardingSteps = () => {
  const totalSteps = 8;

  const [loading, setLoading] = useState(false);

  const { user, onFetch: refetchUser } = useUser();

  const steps = useMemo(() => {
    return user?.onboard_steps || [];
  }, [user]);

  const updateStep = useCallback(
    async (step, status, current) => {
      try {
        setLoading(true);
        await api.put("/api/user/update_onboard_step", {
          step,
          status,
          current,
        });
        await refetchUser();
      } catch (error) {
        alert(error.message);
      } finally {
        setLoading(false);
      }
    },
    [refetchUser]
  );

  const skip = (step, current) => updateStep(step, "Skipped", current);
  const complete = (step, current) => updateStep(step, "Completed", current);

  const getIsStepProcessed = useCallback(
    step =>
      ["Completed", "Skipped"].includes(
        steps.find(i => i.step === step)?.status
      ),
    [steps]
  );

  const processedSteps = useMemo(
    () => steps.filter(s => ["Completed", "Skipped"].includes(s.status)),
    [steps]
  );

  const progress = useMemo(
    () => `${Math.round((processedSteps.length / totalSteps) * 100)}%`,
    [processedSteps, totalSteps]
  );

  const currentStep = useMemo(() => steps.find(i => i.current), [steps]);

  const allCompleted = processedSteps.length === totalSteps;

  return {
    allCompleted,
    currentStep,
    progress,
    steps,
    onSkip: skip,
    onComplete: complete,
    getIsStepProcessed,
    loading,
  };
};

export default useOnboardingSteps;
